import { User as UserIcon } from 'lucide-react';
import { useCallback, useState, type FocusEvent, type KeyboardEvent } from 'react';

import { cn } from '@/shared/cn';
import { FIELD_INPUT } from '@/shared/ui-class';

const USER_NAME_MAX_CHARS = 100;

export function AccountNameField({
  initialName,
  onSave,
}: {
  /** Raw display name — not display-normalised. */
  initialName: string;
  onSave?: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const [savedName, setSavedName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const dirty = trimmedName !== savedName.trim();
  const canSave = trimmedName.length > 0 && dirty;

  const save = useCallback(() => {
    if (!canSave) return;
    setName(trimmedName);
    setSavedName(trimmedName);
    setError(null);
    onSave?.(trimmedName);
  }, [canSave, onSave, trimmedName]);

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (event.relatedTarget instanceof HTMLElement) {
      const saveButton = event.currentTarget
        .closest('[data-account-name-row]')
        ?.querySelector<HTMLButtonElement>('[data-account-name-save]');
      if (saveButton && event.relatedTarget === saveButton) {
        return;
      }
    }
    if (canSave) save();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      save();
    }
    if (event.key === 'Escape') {
      setName(savedName);
      setError(null);
      event.currentTarget.blur();
    }
  };

  return (
    <div
      data-account-name-row
      className="flex items-start gap-4 border-b border-[var(--glass-border)] px-5 py-4 last:border-b-0"
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
        <UserIcon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <label
            htmlFor="account-display-name"
            className="text-xs font-medium uppercase tracking-wide text-muted"
          >
            Name
          </label>
          <input
            id="account-display-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value.slice(0, USER_NAME_MAX_CHARS));
              setError(null);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoComplete="name"
            spellCheck={false}
            className={cn('mt-1 w-full', FIELD_INPUT)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'account-name-error' : undefined}
          />
        </div>
        {error ? (
          <p
            id="account-name-error"
            className="text-xs text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        ) : null}
        <div className="flex justify-end">
          <button
            type="button"
            data-account-name-save
            onClick={save}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
